/**
 * Call flows — the routing graph bound to a phone number.
 *
 * Its own file rather than more lines in `types.ts`: a flow is a graph, and the
 * branch-key rules below are the part the UI has to get right. They mirror
 * `callFlowGraph.js` in the calling service exactly, and where they disagree the
 * calling service wins — it re-validates everything on save and on publish.
 */

import type { IvrSchedule } from './types';

export type CallFlowStatus = 'draft' | 'live' | 'archived';

export type CallFlowStepType =
  | 'business_hours'
  | 'menu'
  | 'ring_users'
  | 'play_audio'
  | 'voicemail'
  | 'forward'
  | 'ai_receptionist'
  | 'hangup';

/** Digits a menu branch may be keyed on — the IVR's own gather window. */
export const MENU_DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'] as const;

/**
 * Why Iris hands a call to a person. One branch per reason, so "who picks up
 * when somebody asks for a human" is wired on the canvas like every other
 * outcome instead of in a separate rule form with its own ring ladder.
 *
 * The keys are the ai-operations trigger names lowercased — the mapping back is
 * `ESCALATION_TRIGGER_BY_BRANCH` in `lib/ai/handoff-flow.ts`, which is what
 * turns a wired branch into a stored escalation rule.
 */
export const HANDOFF_TRIGGERS = [
  'human_request',
  'negative_sentiment',
  'pricing',
  'no_slots',
  'low_confidence',
  'reschedule_threshold',
] as const;
export type HandoffTrigger = (typeof HANDOFF_TRIGGERS)[number];

/**
 * The branches each step type may name, excluding `default`.
 *
 * A type absent here is terminal: the call leaves the flow when it runs, so a
 * branch out of it would point at a step that can never happen.
 */
export const BRANCH_KEYS: Partial<Record<CallFlowStepType, readonly string[]>> = {
  business_hours: ['open', 'closed', 'holiday'],
  menu: [...MENU_DIGITS, 'timeout', 'invalid'],
  ring_users: ['no_answer'],
  forward: ['no_answer'],
  play_audio: [],
  ai_receptionist: [...HANDOFF_TRIGGERS],
};

/**
 * Branches valid on a step, including `default` where it applies.
 *
 * A menu gets NO default, and that is load-bearing rather than an oversight: a
 * menu step's branches override the referenced IVR menu's own options, so a
 * catch-all would silently disable every digit of a menu nobody touched here.
 * Without it a digit is either wired in this builder or runs the menu's own
 * action — nothing in between.
 */
export function allowedBranchKeys(type: CallFlowStepType): string[] {
  const keys = BRANCH_KEYS[type];
  if (!keys) return [];
  // Iris joins the menu in having no `default`. A catch-all would read as "and
  // otherwise hand over to these people", but Iris hands over only for a
  // REASON — an unwired trigger already has a meaning (ring the whole account),
  // and a default would silently claim every reason nobody wired.
  if (type === 'menu' || type === 'ai_receptionist') return [...keys];
  return [...keys, 'default'];
}

/**
 * The branches a step ALWAYS shows a slot for — the outcomes that exist whether
 * or not anyone configures them.
 *
 * A menu has none, and that is the important entry. Its keypad options are
 * opt-in: showing twelve digits plus two situations means fourteen slots on one
 * node, which is unreadable and, worse, implies the menu needs all of them. Most
 * menus override two or three keys and let the rest run the menu's own options.
 *
 * `default` is deliberately absent for ring_users and forward. On those it means
 * the same thing as `no_answer`, and offering both invites someone to wire two
 * branches that fight over one outcome.
 */
/**
 * A complete NANP number in the form PhoneInput emits.
 *
 * It emits `+1` plus whatever digits are typed so far, so a half-entered number
 * looks like `+1555` — truthy, and previously enough to be added to a flow as a
 * number it answers. This is the gate that stops that.
 */
export function isCompleteNanp(value: string | null | undefined): boolean {
  return /^\+1\d{10}$/.test((value ?? '').trim());
}

export function primaryBranches(type: CallFlowStepType): string[] {
  switch (type) {
    case 'business_hours':
      return ['open', 'closed'];
    case 'ring_users':
    case 'forward':
      return ['no_answer'];
    case 'play_audio':
      return ['default'];
    // Asking for a person is the reason nearly every tenant wires, and the one
    // Iris raises whether or not anybody configured it. The rest are opt-in, so
    // the node does not open with six empty slots.
    case 'ai_receptionist':
      return ['human_request'];
    default:
      return [];
  }
}

/**
 * Branches a step CAN have but only shows once added — the "Add keypad option"
 * set. Ordered as they are offered in the picker.
 */
export function optionalBranches(type: CallFlowStepType): string[] {
  switch (type) {
    case 'menu':
      return [...MENU_DIGITS, 'timeout', 'invalid'];
    case 'business_hours':
      return ['holiday'];
    case 'ai_receptionist':
      return HANDOFF_TRIGGERS.filter((t) => t !== 'human_request');
    default:
      return [];
  }
}

/** What the "add a branch" control is called on this step type. */
export function addBranchLabel(type: CallFlowStepType): string {
  if (type === 'menu') return 'Add keypad option';
  if (type === 'ai_receptionist') return 'Add another reason';
  return 'Add another outcome';
}

/**
 * Steps that end the call's time in the flow.
 *
 * Iris used to be here. It is not terminal any more: it answers the caller and,
 * when it hands over, follows the branch for the reason it handed over on. A
 * call that Iris resolves still ends here — no branch is taken — which is why
 * an unwired Iris step is complete on its own and raises no problem.
 */
export const TERMINAL_TYPES: CallFlowStepType[] = ['voicemail', 'hangup'];

/** One agent group a `ring_users` step dials, in order. */
export interface CallFlowRingGroup {
  /** What the author calls this group ("Front desk", "Techs") — display only. */
  name?: string | null;
  /** notifi-1.0 agent ids. */
  userIds?: string[];
  /** notifi-2.0 tenant user ids — the ones this app writes. */
  notifi2UserIds?: string[];
  /**
   * Desk phones this group rings, by landline id. A landline belongs to the
   * account rather than a person, so it carries no user id and can never appear
   * in the two lists above.
   */
  landlineIds?: string[];
  /** How long this group rings before the next one. */
  seconds?: number | null;
}

export interface CallFlowStep {
  /** Stable across edits. Every branch in the flow points at this. */
  id: string;
  type: CallFlowStepType;
  /** What the author called it — shown on the canvas and in call reports. */
  label?: string | null;
  position?: { x: number; y: number };

  /**
   * menu — EITHER a reference to an existing IVR tree, OR the menu itself.
   *
   * `callTreeId` is the older shape and still works: a flow built when menus
   * lived on their own tab keeps pointing at the one it was built with. A step
   * with a greeting needs no tree at all — the calling service builds the menu
   * from the step, and the spoken options come from the keys wired on this very
   * step rather than from a second document that has to be kept in step.
   */
  callTreeId?: string | null;
  greetingMessage?: string | null;
  greetingAudioUrl?: string | null;
  /**
   * Whether the runtime reads the keys out after the greeting.
   *
   * Undefined means yes, which is what every menu has always done — only an
   * explicit false changes anything. Turn it off when the greeting names the
   * keys itself, or the caller hears the menu twice.
   */
  announceOptions?: boolean;
  timeoutDuration?: number | null;
  extensionDialing?: {
    enabled: boolean;
    minDigits?: number;
    maxDigits?: number;
    notFoundMessage?: string | null;
  };
  /** business_hours — same shape as a menu's own schedule. */
  schedule?: IvrSchedule;
  /** ring_users */
  groups?: CallFlowRingGroup[];
  ringTimeoutSecs?: number | null;
  /**
   * ring_users — a cap on the whole step, across every group, in seconds
   * (10–600). A group that would run past it is cut short; one that would get
   * under 5 seconds is skipped and the step's "if nobody answers" runs.
   */
  maxRingSecs?: number | null;
  /** ring_users — leave out people who are already on a call. Desk phones always ring. */
  skipBusy?: boolean;
  /**
   * ring_users — what the caller hears while this step rings, in place of the
   * account's ringing sound. Empty means the account's.
   *
   * voicemail reuses `greetingMessage` / `greetingAudioUrl` above as its own
   * greeting (recording wins); both empty means the account's voicemail greeting.
   */
  ringbackAudioUrl?: string | null;
  /**
   * business_hours — what the caller hears ringing for the rest of the flow
   * once this step has picked a branch: one sound for "During hours", one for
   * "After hours" (holidays too). Empty means the account's ringing sound. A
   * ring step's own `ringbackAudioUrl` still wins over these.
   */
  openRingbackAudioUrl?: string | null;
  closedRingbackAudioUrl?: string | null;
  /**
   * voicemail — whose voicemail this is (a user id). Their messages are
   * assigned to them in Action Needed, with a notification. Empty is the shared
   * voicemail, which nobody owns until someone picks it up.
   *
   * Arrives at operations under a DIFFERENT name: the calling service carries it
   * onto the call and sends it as `metadata.voicemailFor` on `call.voicemail` —
   * see `CallLifecycleMetadata` in apps/operations for the whole chain.
   */
  voicemailForUserId?: string | null;
  /** forward */
  target?: string | null;
  /** play_audio — `audioUrl` wins when both are set. */
  message?: string | null;
  audioUrl?: string | null;
  voice?: string | null;
  language?: string | null;

  /** Branch key → the id of the step it leads to. */
  next?: Record<string, string>;
}

export interface CallFlow {
  _id: string;
  name: string;
  status: CallFlowStatus;
  /** Canonical E.164. A flow answers only the numbers it names. */
  phoneNumbers: string[];
  entryStepId: string | null;
  steps: CallFlowStep[];
  version?: number;
  publishedAt?: string | null;
  /** A draft scheduled to go live at this time (ISO), or null. */
  publishAt?: string | null;
  /** Why the last scheduled publish did not go ahead, or null. */
  scheduleError?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Create and update take the same shape; every field is optional on update. */
export interface UpsertCallFlowPayload {
  name?: string;
  phoneNumbers?: string[];
  entryStepId?: string | null;
  steps?: CallFlowStep[];
}

// ─── Presentation ────────────────────────────────────────────────────────────

/**
 * How each step type reads to a person.
 *
 * `rings` is not decoration: it drives the warning shown before publish. A flow
 * answers a number ABOVE the account-wide hours check, so any path that reaches
 * one of these without passing a business-hours step will ring somebody at 3am,
 * and the calling service refuses to publish it.
 */
export const STEP_META: Record<CallFlowStepType, { label: string; blurb: string; rings: boolean }> =
  {
    business_hours: {
      label: 'Business hours',
      blurb: 'Split the call by whether you are open',
      rings: false,
    },
    menu: {
      label: 'Phone menu',
      blurb: 'Route callers based on the keys they press',
      rings: true,
    },
    ring_users: {
      label: 'Ring people',
      blurb: 'Ring one or more groups, in order',
      rings: true,
    },
    play_audio: {
      label: 'Play a message',
      blurb: 'Say something, then carry on',
      rings: false,
    },
    forward: {
      label: 'Forward',
      blurb: 'Send the call to another number',
      rings: true,
    },
    voicemail: { label: 'Voicemail', blurb: 'Take a message', rings: false },
    ai_receptionist: {
      label: 'Iris',
      blurb: 'Hand the caller to the assistant',
      rings: true,
    },
    hangup: { label: 'Hang up', blurb: 'End the call', rings: false },
  };

/** How a branch key reads on a connector. */
export function branchLabel(key: string): string {
  switch (key) {
    case 'open':
      return 'During hours';
    case 'closed':
      return 'After hours';
    case 'holiday':
      return 'On a holiday';
    case 'no_answer':
      return 'If nobody answers';
    case 'timeout':
      return 'If they press nothing';
    case 'invalid':
      return 'If they press something else';
    case 'default':
      return 'Then';
    // Iris handoff reasons, worded as the moment they happen on a live call.
    case 'human_request':
      return 'If they ask for a person';
    case 'negative_sentiment':
      return 'If they are unhappy';
    case 'pricing':
      return 'If they haggle on price';
    case 'no_slots':
      return 'If nothing is available to book';
    case 'low_confidence':
      return 'If Iris is unsure';
    case 'reschedule_threshold':
      return 'If they have rescheduled too often';
    default:
      return `Press ${key}`;
  }
}
